import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Badge, message, Empty } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { getFilteredCoversOrMedia } from 'helpers/contentlibrary';
import { GET_ALL_MEDIAS_LIBRARIES } from 'graphql/queries/contentLibraryQueries';
import {
  UPDATE_CONTENT_LIBRARY,
  CONTENT_LIBRARY_MEDIA_DELETE,
} from 'graphql/mutations/contentLibraryMutations';
import { ContentLoader } from '../ContentLoader/ContentLoader';
import { useContentLibrary } from 'context/ContentLibraryContext';

import ConfirmModal from 'components/ConfirmModal';
import ContentLibraryTree from '../ContentLibraryTree/ContentLibraryTree';
import CoverOrMediaCard from '../CoverOrMediaCard/CoverOrMediaCard';

/**
 * Media tab
 *
 * @param {Object} props
 * @param {Array} props.filterItems
 * @param {Object} props.user
 * @param {string} props.searchValue
 * @param {string} props.sortValue
 * @param {boolean} props.isLocked
 */
const MediaTab = forwardRef(({ filterItems, user, searchValue, sortValue, isLocked }, ref) => {
  // Get treeData and related functions from context
  const { treeData, selectedFolderKey, onContentLibraryKeySelect } = useContentLibrary();

  const [medias, setMedias] = useState([]);
  const [selectedMediaId, setSelectedMediaId] = useState('');
  const [selectedMediaEditValue, setSelectedMediaEditValue] = useState('');
  const [selectedMediaStarred, setSelectedMediaStarred] = useState('');
  const [confirmModal, showConfirmModal] = useState(false);

  const { loading: isFetchingMedias, refetch: refetchMedias } = useQuery(GET_ALL_MEDIAS_LIBRARIES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      type: 'medias',
    },
    onCompleted: ({ fetchContentLibraryMediasItems }) => {
      setMedias(fetchContentLibraryMediasItems.slice());
    },
  });

  /**
   * Update content item mutation
   */
  const [updateContentItem] = useMutation(UPDATE_CONTENT_LIBRARY, {
    async onCompleted() {
      setMedias(
        JSON.parse(
          JSON.stringify(
            medias.map((media) =>
              media._id === selectedMediaId
                ? selectedMediaStarred !== ''
                  ? { ...media, starred: [selectedMediaStarred.toString()] }
                  : { ...media, name: selectedMediaEditValue ? selectedMediaEditValue : media.name }
                : media
            )
          )
        )
      );

      setSelectedMediaId('');
      setSelectedMediaEditValue('');
      setSelectedMediaStarred('');
    },
  });

  /**
   * Delete media mutation
   */
  const [deleteMedia] = useMutation(CONTENT_LIBRARY_MEDIA_DELETE, {
    onCompleted() {
      let tempMedias = medias.filter((media) => media._id !== selectedMediaId);
      setMedias(tempMedias);
      message.success('Media deleted successfully');
      showConfirmModal(false);
      setSelectedMediaId('');
    },
    onError() {
      message.error('Media deletion failed');
      showConfirmModal(false);
    },
  });

  /**
   * Use imperative handle
   */
  useImperativeHandle(ref, () => ({
    async reloadItems() {
      await refetchMedias();
    },
  }));

  /**
   * Use effect
   */
  useEffect(() => {
    refetchMedias({
      type: 'medias',
      key: selectedFolderKey,
    });
  }, [refetchMedias, selectedFolderKey]);

  /**
   * Filtered medias
   */
  const filteredMedias = getFilteredCoversOrMedia({
    medias,
    filterItems,
    searchValue,
    sortValue,
  });

  /**
   * Set selected folder key
   */
  localStorage.setItem('selectedFolderKey', selectedFolderKey);

  const { starred: starredItems, all: allItems } = filteredMedias;
  const isMediasEmpty = !(filteredMedias.starred.length || filteredMedias.all.length);

  /**
   * Star or unstar
   *
   * @param {Object} params
   * @param {boolean} params.starred
   * @param {string} params.mediaId
   */
  const starOrUnstar = ({ starred, mediaId }) => {
    updateContentItem({
      variables: {
        id: mediaId,
        type: 'medias',
        item: { starred },
      },
    });
  };

  /**
   * Save media title
   *
   * @param {Object} params
   * @param {string} params.mediaId
   */
  const saveMediaTitle = ({ mediaId }) => {
    updateContentItem({
      variables: {
        id: mediaId,
        type: 'medias',
        item: { name: selectedMediaEditValue },
      },
    });
  };

  /**
   * Confirm delete media
   */
  const confirmDeleteMedia = () => {
    deleteMedia({
      variables: {
        _id: selectedMediaId,
        folderKey: selectedFolderKey,
      },
    });
  };

  /**
   * On click menu item
   *
   * @param {Object} params
   * @param {string} params.actionType
   * @param {string} params.mediaId
   * @param {boolean} params.starred
   * @param {string} params.name
   */
  const onClickMenuItem = async ({ actionType, _id: mediaId, starred, name }) => {
    setSelectedMediaId(mediaId);
    if (actionType === 'delete') {
      showConfirmModal(true);
    } else if (actionType === 'rename') {
      setSelectedMediaEditValue(name);
    } else if (actionType === 'starOrUnstar') {
      setSelectedMediaStarred(starred);
      starOrUnstar({ starred, mediaId });
    } else if (actionType === 'save') {
      saveMediaTitle({ mediaId });
    } else {
      message.warning('Coming soon 😊');
    }
  };

  /**
   * On key select
   */
  const onKeySelect = (selectedFolderKey) => {
    onContentLibraryKeySelect(selectedFolderKey);
  };

  /**
   * Render
   */
  return (
    <div className="content-library-item">
      <ContentLibraryTree
        onKeySelect={onKeySelect}
        type="media"
        onUpdate={() =>
          refetchMedias({
            type: 'medias',
            key: selectedFolderKey,
          })
        }
        userId={user && user._id}
        userRole={user?.roles && user?.roles[0]}
        isLocked={isLocked}
        treeData={treeData}
      />
      <div className="split-d2">
        {isFetchingMedias ? (
          <ContentLoader />
        ) : (
          <>
            {starredItems.length ? (
              <div className="stared-header">
                MY STARRED MEDIAS
                <Badge className="star-badge" count={starredItems.length} />
              </div>
            ) : null}

            <div className="scroll-wrapper">
              <React.Fragment>
                {isMediasEmpty && (
                  <div className="item-row empty-wrapper">
                    <Empty
                      className="empty-view"
                      description="Looks like you have no media files yet"
                    />
                  </div>
                )}
                <div className="item-row medias-card">
                  {starredItems.map((item, itemIndex) => {
                    return (
                      <CoverOrMediaCard
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData('data', JSON.stringify(item));
                        }}
                        item={item}
                        type="media"
                        key={item._id}
                        index={itemIndex}
                        userId={user && user._id}
                        userRole={user?.roles && user?.roles[0]}
                        onClickMenuItem={onClickMenuItem}
                        itemEditable={selectedMediaId === item._id}
                        setEditValue={setSelectedMediaEditValue}
                        editValue={selectedMediaEditValue}
                      />
                    );
                  })}
                </div>
                {starredItems.length ? <div className="divider-line" /> : null}
                <div className="item-row medias-card">
                  {allItems.map((item, itemIndex) => {
                    return (
                      <CoverOrMediaCard
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData('data', JSON.stringify(item));
                        }}
                        item={item}
                        type="media"
                        key={item._id}
                        index={itemIndex}
                        userId={user && user._id}
                        userRole={user?.roles && user?.roles[0]}
                        onClickMenuItem={onClickMenuItem}
                        itemEditable={selectedMediaId === item._id}
                        setEditValue={setSelectedMediaEditValue}
                        editValue={selectedMediaEditValue}
                      />
                    );
                  })}
                </div>
              </React.Fragment>
            </div>
          </>
        )}
      </div>
      <ConfirmModal
        title="Delete media?"
        body="This action cannot be undone"
        visible={confirmModal}
        cancelText="CANCEL"
        confirmText="YES, DELETE"
        onCancel={() => showConfirmModal(false)}
        onConfirm={confirmDeleteMedia}
      />
    </div>
  );
});

/**
 * Default props
 */
MediaTab.defaultProps = {
  searchValue: '',
  isLocked: false,
};

/**
 * Prop types
 */
MediaTab.propTypes = {
  filterItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.instanceOf(Object).isRequired,
  searchValue: PropTypes.string,
  sortValue: PropTypes.string.isRequired,
  isLocked: PropTypes.bool,
};

export default MediaTab;
